<template>
  <v-dialog :value="value" max-width="560" persistent @input="onDialogInput">
    <v-card class="add-to-group-dialog">
      <v-card-title class="px-6">
        <h5 class="primary-color mb-0">Add to group</h5>
        <v-spacer />
        <v-btn icon aria-label="Close" :disabled="adding" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="px-6 py-2">
        <v-select
          v-model="groupId"
          label="Analysis group *"
          :items="groupItems"
          item-text="text"
          item-value="value"
          :loading="loadingGroups"
          :disabled="adding || Boolean(lockedGroupId)"
          dense
          outlined
          hide-details="auto"
          class="mb-3 add-to-group-dialog__group"
          attach
        >
          <template #append-item>
            <v-divider class="my-1" />
            <v-list-item class="add-to-group-dialog__new" @click="$emit('create-group')">
              <v-list-item-icon class="mr-2"><v-icon small>mdi-plus</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>New group…</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-select>

        <v-tabs v-model="mode" dense class="add-to-group-dialog__tabs mb-3">
          <v-tab href="#selection" :disabled="!items.length" class="text-none">
            Selection ({{ items.length }})
          </v-tab>
          <v-tab v-if="allowFolder" href="#folder" class="text-none">From folder</v-tab>
        </v-tabs>

        <div v-if="mode === 'selection'" class="add-to-group-dialog__selection">
          <p v-if="!items.length" class="caption grey--text mb-0">
            Select one or more videos in the list first.
          </p>
          <template v-else>
            <v-chip
              v-for="item in visibleItems"
              :key="`${item.source_type}-${item.source_id}`"
              x-small
              outlined
              label
              class="mr-1 mb-1"
            >
              {{ memberLabel(item) }}
            </v-chip>
            <span v-if="items.length > visibleItems.length" class="caption grey--text">
              and {{ items.length - visibleItems.length }} more
            </span>
          </template>
        </div>

        <div v-else class="add-to-group-dialog__folder">
          <v-select
            v-model="folderId"
            label="Folder *"
            :items="folderItems"
            item-text="text"
            item-value="value"
            :loading="loadingFolders"
            :disabled="adding"
            dense
            outlined
            hide-details="auto"
            attach
          />
          <p class="caption grey--text mt-2 mb-0">
            Every manual video of the folder becomes a member (already added videos are skipped).
          </p>
        </div>

        <v-alert v-if="error" dense text type="error" class="mt-3 mb-0">{{ error }}</v-alert>
      </v-card-text>

      <v-divider />
      <v-card-actions class="px-6 py-3">
        <v-spacer />
        <v-btn text class="cancel-modal" :disabled="adding" @click="close">Cancel</v-btn>
        <v-btn
          class="ok-modal add-to-group-dialog__add"
          :disabled="!canAdd"
          :loading="adding"
          @click="add"
        >
          Add{{ countLabel }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import AnalysisGroupsService from '@/services/AnalysisGroupsService';
import ManualAnalysisService from '@/services/ManualAnalysisService';
import { memberLabel } from '@/utils/groupResolution';

/**
 * Adds videos to an analysis group (M4 contract 4): either the current selection
 * (`POST /:id/members`) or a whole manual folder (`POST /:id/members/from-folder`). Usable from
 * the groups list, the manual video list and the candidates list. Emits `added`
 * `{ group_id, added, skipped }`.
 */
export default {
  name: 'AddToGroupDialog',

  props: {
    value: { type: Boolean, default: false },
    /** Current selection: [{ source_type, source_id, name? }]. */
    items: { type: Array, default: () => [] },
    /** Groups to choose from; loaded from the API when null. */
    groups: { type: Array, default: null },
    /** Preselects (and locks) a group, e.g. when opened from a group page. */
    lockedGroupId: { type: [Number, String], default: null },
    /** Shows the 'From folder' tab (manual videos only). */
    allowFolder: { type: Boolean, default: true },
  },

  data() {
    return {
      groupId: null,
      mode: 'selection',
      folderId: null,
      loadedGroups: [],
      loadingGroups: false,
      folders: [],
      loadingFolders: false,
      adding: false,
      error: '',
    };
  },

  computed: {
    availableGroups() {
      return Array.isArray(this.groups) ? this.groups : this.loadedGroups;
    },
    groupItems() {
      return this.availableGroups.map((group) => ({
        value: Number(group.id),
        text: `${group.name}${group.members != null ? ` (${group.members} videos)` : ''}`,
      }));
    },
    folderItems() {
      return this.folders.map((folder) => ({ value: folder.id, text: folder.name }));
    },
    visibleItems() {
      return this.items.slice(0, 12);
    },
    canAdd() {
      if (this.adding || !this.groupId) return false;
      if (this.mode === 'folder') return Boolean(this.folderId);
      return this.items.length > 0;
    },
    countLabel() {
      if (this.mode === 'folder') return ' folder';
      return this.items.length
        ? ` ${this.items.length} ${this.items.length === 1 ? 'video' : 'videos'}`
        : '';
    },
  },

  watch: {
    value(open) {
      if (open) this.reset();
    },
    mode(mode) {
      if (mode === 'folder' && !this.folders.length) this.loadFolders();
    },
  },

  created() {
    if (this.value) this.reset();
  },

  methods: {
    memberLabel,

    reset() {
      this.error = '';
      this.adding = false;
      this.groupId = this.lockedGroupId != null ? Number(this.lockedGroupId) : null;
      this.mode = this.items.length ? 'selection' : this.allowFolder ? 'folder' : 'selection';
      this.folderId = null;
      if (!Array.isArray(this.groups)) this.loadGroups();
      if (this.mode === 'folder') this.loadFolders();
    },

    async loadGroups() {
      this.loadingGroups = true;
      try {
        const response = await AnalysisGroupsService.list({ limit: 200 });
        const body = response?.data?.data ?? response?.data ?? {};
        this.loadedGroups = Array.isArray(body.items)
          ? body.items
          : Array.isArray(body)
            ? body
            : [];
        if (!this.groupId && this.loadedGroups.length === 1) {
          this.groupId = Number(this.loadedGroups[0].id);
        }
      } catch (error) {
        this.loadedGroups = [];
      } finally {
        this.loadingGroups = false;
      }
    },

    async loadFolders() {
      this.loadingFolders = true;
      try {
        const response = await ManualAnalysisService.getFolders();
        const body = response?.data?.data ?? response?.data ?? [];
        this.folders = Array.isArray(body) ? body : Array.isArray(body.items) ? body.items : [];
      } catch (error) {
        this.folders = [];
      } finally {
        this.loadingFolders = false;
      }
    },

    onDialogInput(open) {
      if (!open) this.close();
    },

    close() {
      if (this.adding) return;
      this.$emit('input', false);
    },

    async add() {
      if (!this.canAdd) return;
      this.adding = true;
      this.error = '';
      try {
        const response =
          this.mode === 'folder'
            ? await AnalysisGroupsService.fromFolder(this.groupId, this.folderId)
            : await AnalysisGroupsService.addMembers(this.groupId, this.items);
        const body = response?.data?.data ?? response?.data ?? {};
        const result = {
          group_id: this.groupId,
          added: Array.isArray(body.added) ? body.added.length : (body.added ?? body.count ?? null),
          skipped: Array.isArray(body.skipped) ? body.skipped.length : (body.skipped ?? 0),
        };
        this.$emit('added', result);
        this.$emit('input', false);
      } catch (error) {
        const data = error?.response?.data;
        this.error = data?.message || data?.error || error?.message || 'Could not add the videos.';
      } finally {
        this.adding = false;
      }
    },
  },
};
</script>
